import type { ShowPopupOptions, ShowPopupOptionsButton } from '../../bootstrap/tmaActions';

/** Telegram's own default when a caller passes no buttons at all. */
const DEFAULT_BUTTONS: ShowPopupOptionsButton[] = [{ type: 'close' }];

export type ConfirmButtonKind = 'default' | 'destructive' | 'ok' | 'close' | 'cancel';

export interface ConfirmButton {
  /** Caller-supplied id, `null` when omitted. */
  id: string | null;
  kind: ConfirmButtonKind;
  /** Caller-supplied label; absent on `ok` / `close` / `cancel`, where
   *  Telegram supplies its own localized text and we supply ours. */
  text?: string;
}

export const isRefusalButton = (button: ConfirmButton): boolean =>
  button.kind === 'cancel' || button.id === 'cancel';

/**
 * What the caller gets back when this button is pressed. Telegram answers with
 * the button id; for a button whose id the caller omitted we answer with the
 * type name, so `cancel` still reads as a refusal instead of "no answer".
 */
export const confirmAnswerId = (button: ConfirmButton): string | null => {
  if (button.id) return button.id;
  return button.kind === 'default' || button.kind === 'destructive' ? null : button.kind;
};

/**
 * Buttons to draw, refusal first.
 *
 * Call sites pass both orders — `PaymentScreen` puts cancel first,
 * `WheelScreen` puts confirm first — and in a vertical stack that would move
 * the safe answer around between screens. Pinning refusal to the top keeps one
 * shape everywhere.
 */
export function confirmButtons(options: ShowPopupOptions): ConfirmButton[] {
  const raw = options.buttons?.length ? options.buttons : DEFAULT_BUTTONS;
  const parsed: ConfirmButton[] = raw.map((button) => ({
    id: button.id ?? null,
    kind: 'text' in button ? (button.type ?? 'default') : button.type,
    text: 'text' in button ? button.text : undefined,
  }));
  return [...parsed.filter(isRefusalButton), ...parsed.filter((b) => !isRefusalButton(b))];
}

/**
 * Answer for a close that went past the buttons — backdrop, Escape, swipe
 * down, the corner cross, browser back.
 *
 * On money screens a mis-read close is expensive, so it always means refusal:
 * the refusal button's answer when there is one, otherwise no answer at all
 * (`null`) — which every call site already reads as "not confirmed". A
 * single-button popup is informational, so closing it IS pressing it.
 */
export function dismissAnswer(options: ShowPopupOptions): string | null {
  const buttons = confirmButtons(options);
  if (buttons.length === 1) return confirmAnswerId(buttons[0]);
  const refusal = buttons.find(isRefusalButton);
  return refusal ? (refusal.id ?? 'cancel') : null;
}
